import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import { Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/modules/userSlice";
import { DownloadFormHtmlToPdf, FormfAPI } from "../components/api";
import useDraft from "../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../config/joineeFormConstants";
import JoineeFormShell, {
  JoineeFormFooterActions,
} from "../components/NewJoineForm/JoineeFormShell";
import {
  FormSectionTitle,
  FormTextField,
  FormSelectField,
  FormDateField,
  FormTwoCol,
  FormCheckboxField,
  FormAddButton,
} from "../components/NewJoineForm/JoineeFormFields";
import ApprovedCustomizedDialogs from "../components/Model/ModelApproved";
import {
  canShowHrApprove,
  canShowHrResubmit,
  canShowHrReviewActions,
  isJoineeEmployeeFormLocked,
} from "../config/joineeConstants";
import ReSubmitCustomizedDialogs from "../components/Model/ModelReSubmit";
import ReportValue from "../components/Shared/ReportValue";
import { MediaPreviewDialog } from "../components/Shared/MediaPreview";
import LoadingButtons from "../components/Model/LoadingButtonModel";
import {
  isEmployerCertificateReady,
  isHrLockedField,
  pickHrOnboardingSnapshot,
  applyHrLockedFields,
} from "../utils/hrFieldAccess";
import HrResubmitNotice from "../components/Joinee/HrResubmitNotice";
import {
  resolveCompanyName,
  resolveCompanyAddress,
} from "../config/companyConfig";
import { preferSavedDate, isFutureDate } from "../utils/dateHelpers";
import { ruleSchema, validateDraft, notifyFormInvalid } from "../utils/inputRules";

var EMPTY_DISPLAY = "—";
var MAX_NOMINEES = 4;

var RELATION_OPTIONS = [
  { value: "", label: "Select Relationship" },
  { value: "Mother", label: "Mother" },
  { value: "Father", label: "Father" },
  { value: "Spouse", label: "Spouse" },
  { value: "Daughter", label: "Daughter" },
  { value: "Son", label: "Son" },
  { value: "Sister", label: "Sister" },
  { value: "Brother", label: "Brother" },
  { value: "Mother-in-law", label: "Mother-in-law" },
  { value: "Father-in-law", label: "Father-in-law" },
  { value: "Guardian", label: "Guardian" },
  { value: "Other", label: "Other" },
];

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function displayValue(value) {
  if (value === null || value === undefined) {
    return EMPTY_DISPLAY;
  }
  var text = String(value).trim();
  return text ? text : EMPTY_DISPLAY;
}

function formatDob(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : displayValue(value);
}

function formatMarital(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var v = String(value).toLowerCase();
  if (v === "married") return "Married";
  if (v === "single" || v === "unmarried") return "Unmarried";
  if (v === "widow") return "Widow";
  if (v === "widower") return "Widower";
  if (v === "divorcee") return "Divorcee";
  return displayValue(value);
}

function formatGender(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var v = String(value).toLowerCase();
  if (v === "male") return "Male";
  if (v === "female") return "Female";
  if (v === "prefer not to answer" || v === "na") return "Prefer not to answer";
  return displayValue(value);
}

function normalizeRelation(value) {
  if (!value) {
    return "";
  }
  var found = RELATION_OPTIONS.find(function (opt) {
    return (
      opt.value &&
      String(opt.value).toLowerCase() === String(value).toLowerCase()
    );
  });
  return found ? found.value : String(value);
}

function withRelationOption(value) {
  var normalized = normalizeRelation(value);
  if (!normalized) {
    return RELATION_OPTIONS;
  }
  var exists = RELATION_OPTIONS.some(function (opt) {
    return opt.value === normalized;
  });
  if (exists) {
    return RELATION_OPTIONS;
  }
  return RELATION_OPTIONS.concat([
    { value: normalized, label: normalized },
  ]);
}

function emptyNominee() {
  return {
    name: "",
    address: "",
    reln: "",
    age: null,
    gratuity: "",
  };
}

function ReadOnlyRow(props) {
  var [previewItem, setPreviewItem] = useState(null);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "16px", sm: "32px" },
        width: "100%",
        mb: 2,
      }}
    >
      {(props.items || []).map(function (item, index) {
        return (
          <Box
            key={item.label + "-" + index}
            sx={{
              display: "flex",
              flex: "1 1 0",
              gap: "16px",
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#848F9A",
                lineHeight: 1.14,
                width: { xs: "140px", sm: "164px" },
                flexShrink: 0,
              }}
            >
              {item.label}
            </Typography>
            {item.image ? (
              <ButtonBase
                aria-label="View signature"
                title="Click to view"
                onClick={function () {
                  setPreviewItem(item);
                }}
                sx={{ borderRadius: "4px", cursor: "zoom-in" }}
              >
                <Box
                  component="img"
                  src={item.image}
                  alt="Signature"
                  sx={{
                    maxWidth: 120,
                    maxHeight: 48,
                    objectFit: "contain",
                    border: "1px solid " + JOINEE_FORM_COLORS.border,
                    borderRadius: "4px",
                    bgcolor: "#fff",
                  }}
                />
              </ButtonBase>
            ) : (
              <Typography
                sx={{
                  fontFamily: JOINEE_FORM_FONT,
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#171A1C",
                  lineHeight: 1.14,
                  minWidth: 0,
                  wordBreak: "break-word",
                }}
              >
                {item.value}
              </Typography>
            )}
          </Box>
        );
      })}
      <MediaPreviewDialog
        open={Boolean(previewItem)}
        onClose={function () {
          setPreviewItem(null);
        }}
        src={previewItem ? previewItem.image : ""}
        fileName={previewItem ? previewItem.fileName : ""}
        fallbackName="signature"
        title="Signature"
      />
    </Box>
  );
}

function RemoveRowButton(props) {
  return (
    <Button
      type="button"
      variant="outlined"
      onClick={props.onClick}
      disabled={props.disabled}
      sx={{
        fontFamily: JOINEE_FORM_FONT,
        fontSize: 14,
        fontWeight: 400,
        textTransform: "none",
        color: JOINEE_FORM_COLORS.error,
        borderColor: JOINEE_FORM_COLORS.border,
        borderRadius: "6px",
        minHeight: 32,
      }}
    >
      Remove
    </Button>
  );
}

function buildValidationSchema(isAdmin, departmentLocked) {
  var base = {
    employed_department: departmentLocked
      ? Yup.string().trim().required("Department is required")
      : ruleSchema("text", { label: "Department", required: true }),
    ticket_no: ruleSchema("code", { label: "Ticket No." }),
    doa: Yup.mixed().nullable().required("Date of appointment is required"),
    permanentAddress: Yup.object({
      village: ruleSchema("place", { label: "Village", required: true }),
      thane: ruleSchema("place", { label: "Thana", required: true }),
      subDivision: ruleSchema("place", {
        label: "Sub-Division",
        required: true,
      }),
      postOffice: ruleSchema("place", {
        label: "Post-Office",
        required: true,
      }),
      district: ruleSchema("place", { label: "District", required: true }),
      state: ruleSchema("place", { label: "State", required: true }),
    }),
    witnessDetails: Yup.object({
      witnessOneNameAndAddress: ruleSchema("address", {
        label: "Witness one name and address",
      }),
      witnessOneSignature: ruleSchema("personName", {
        label: "Witness one signature",
      }),
      witnessTwoNameAndAddress: ruleSchema("address", {
        label: "Witness two name and address",
      }),
      witnessTwoSignature: ruleSchema("personName", {
        label: "Witness two signature",
      }),
    }),
    nominees: Yup.array()
      .of(
        Yup.object({
          name: ruleSchema("personName", {
            label: "Nominee name",
            required: true,
          }),
          address: ruleSchema("address", {
            label: "Nominee address",
            required: true,
          }),
          reln: Yup.string().trim().required("Relationship is required"),
          age: Yup.mixed()
            .nullable()
            .required("Date of birth is required")
            .test(
              "not-future-dob",
              "Date of birth cannot be in the future",
              function (value) {
                return !isFutureDate(value);
              }
            ),
          gratuity: ruleSchema("percentShare", {
            label: "Gratuity share",
            required: true,
          }),
        })
      )
      .min(1, "Add at least one nominee")
      .max(MAX_NOMINEES, "Maximum " + MAX_NOMINEES + " nominees allowed")
      .test(
        "unique-nominee-names",
        "Nominee names must be unique",
        function (list) {
          if (!list || !list.length) return true;
          var names = list
            .map(function (row) {
              return String((row && row.name) || "")
                .trim()
                .toLowerCase();
            })
            .filter(Boolean);
          return names.length === new Set(names).size;
        }
      )
      .test(
        "share-100",
        "Total gratuity share must equal 100%",
        function (list) {
          if (!list || !list.length) return false;
          var sum = list.reduce(function (acc, row) {
            return acc + Number((row && row.gratuity) || 0);
          }, 0);
          return sum === 100;
        }
      ),
  };

  if (!isAdmin) {
    base.formFLegalConsent = Yup.boolean().oneOf(
      [true],
      "Please confirm that the information provided is true and accurate"
    );
  }

  return Yup.object(base);
}

function FormF(props) {
  var navigate = useNavigate();
  var params = useParams();
  var routeId = params.id ? params.id : "";
  var redux = useSelector(selectUser);
  var user = redux && redux.user ? redux.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? props.id || routeId : user._id;

  var draftApi = useDraft("formF", "main");
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState(false);
  var [formKey, setFormKey] = useState(0);
  var [initialValues, setInitialValues] = useState(null);
  var [formfFlagButton, setFormfFlagButton] = useState("");
  var [approvedComment, setApprovedComment] = useState("");
  var [reSubmitComment, setReSubmitComment] = useState("");
  var [previewOpen, setPreviewOpen] = useState(false);
  var [previewHtml, setPreviewHtml] = useState("");
  var [previewLoading, setPreviewLoading] = useState(false);
  var [passbookFileName, setPassbookFileName] = useState(null);
  var [certSaved, setCertSaved] = useState(false);
  var [hrOnboardingSnapshot, setHrOnboardingSnapshot] = useState({});
  var departmentLocked = isHrLockedField(
    isAdmin,
    hrOnboardingSnapshot.designation
  );

  useEffect(
    function () {
      var isActive = true;
      if (!userId) {
        setLoading(false);
        return function () {
          isActive = false;
        };
      }

      setLoading(true);
      FormfAPI.getformf(userId)
        .then(function (res) {
          if (!isActive) {
            return;
          }
          var data = res && res.data ? res.data : {};
          var userResp = data.userResp || {};
          var formFData = data.formFData || {};
          var sdf = data.selfDeclarationResp || {};
          var todayIso = new Date().toISOString();
          var companyName = userResp.companyName || "";

          var mappedNominees =
            formFData.nominees && formFData.nominees.length
              ? formFData.nominees.map(function (e) {
                  return {
                    name: e.name || "",
                    address: e.address || "",
                    reln: normalizeRelation(e.reln),
                    age: e.age || null,
                    gratuity:
                      e.gratuity === 0 || e.gratuity
                        ? String(e.gratuity)
                        : "",
                  };
                })
              : [emptyNominee()];

          var obj = {
            formfId: formFData._id || "",
            name: userResp.name || "",
            companyName: companyName,
            date: formFData.date || null,
            nominees: mappedNominees,
            emp_name: userResp.name || "",
            gender: userResp.gender || "",
            religion: userResp.religion || "",
            maritalStatus: userResp.maritalStatus || "",
            employed_department:
              formFData.employed_department || userResp.designation || "",
            ticket_no: formFData.ticket_no || "",
            doa: formFData.doa || userResp.doj || null,
            permanentAddress: {
              village: formFData.permanentAddress
                ? formFData.permanentAddress.village || ""
                : "",
              thane: formFData.permanentAddress
                ? formFData.permanentAddress.thane || ""
                : "",
              subDivision: formFData.permanentAddress
                ? formFData.permanentAddress.subDivision || ""
                : "",
              postOffice: formFData.permanentAddress
                ? formFData.permanentAddress.postOffice || ""
                : "",
              district: formFData.permanentAddress
                ? formFData.permanentAddress.district || ""
                : "",
              state: formFData.permanentAddress
                ? formFData.permanentAddress.state || ""
                : "",
            },
            authorization: {
              name: userResp.name || "",
              place: sdf.place || "",
              date: preferSavedDate(
                formFData.authorization && formFData.authorization.date,
                todayIso
              ),
            },
            witnessDetails: {
              witnessOneNameAndAddress: formFData.witnessDetails
                ? formFData.witnessDetails.witnessOneNameAndAddress || ""
                : "",
              witnessOneSignature: formFData.witnessDetails
                ? formFData.witnessDetails.witnessOneSignature || ""
                : "",
              witnessTwoNameAndAddress: formFData.witnessDetails
                ? formFData.witnessDetails.witnessTwoNameAndAddress || ""
                : "",
              witnessTwoSignature: formFData.witnessDetails
                ? formFData.witnessDetails.witnessTwoSignature || ""
                : "",
            },
            certificate_employer: {
              number: formFData.certificate_employer
                ? formFData.certificate_employer.number || ""
                : "",
              officer_authorised_desg: formFData.certificate_employer
                ? formFData.certificate_employer.officer_authorised_desg || ""
                : "",
              date: formFData.certificate_employer
                ? formFData.certificate_employer.date || ""
                : "",
              name_address_establishment: formFData.certificate_employer
                ? formFData.certificate_employer.name_address_establishment || ""
                : "",
            },
            acknowledge_employee: {
              authorization_name: userResp.name || "",
              date: preferSavedDate(
                (formFData.authorization && formFData.authorization.date) ||
                  (formFData.acknowledge_employee &&
                    formFData.acknowledge_employee.date),
                todayIso
              ),
            },
            passbook: formFData.passbook || "",
            passbook_filename: formFData.passbook_filename || "",
            userSignature: userResp.signature || "",
            userSignatureFileName: userResp.signature_fileName || "",
            formFLegalConsent: Boolean(formFData.formFLegalConsent),
            formFLegalConsentAt: formFData.formFLegalConsentAt || null,
          };

          setPassbookFileName(formFData.passbook_filename || null);
          setCertSaved(
            isEmployerCertificateReady(formFData.certificate_employer)
          );
          setHrOnboardingSnapshot(pickHrOnboardingSnapshot(userResp));
          setFormfFlagButton(userResp.isFormFComplete || "");
          setApprovedComment(
            userResp.internal_notes && userResp.internal_notes.formFComment
              ? userResp.internal_notes.formFComment
              : ""
          );
          setReSubmitComment(userResp.formFComment || "");

          if (isAdmin) {
            setInitialValues(obj);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
            return;
          }

          return loadDraft().then(function (draft) {
            if (!isActive) {
              return;
            }
            var merged = applySectionDraftData("formF", "main", obj, draft);
            merged = applyHrLockedFields(
              merged,
              pickHrOnboardingSnapshot(userResp),
              isAdmin
            );
            merged.name = userResp.name || "";
            merged.emp_name = userResp.name || "";
            merged.gender = obj.gender;
            merged.religion = obj.religion;
            merged.maritalStatus = obj.maritalStatus;
            merged.userSignature = obj.userSignature;
            merged.userSignatureFileName = obj.userSignatureFileName;
            if (merged.nominees && merged.nominees.length) {
              merged.nominees = merged.nominees.map(function (e) {
                return Object.assign({}, emptyNominee(), e, {
                  reln: normalizeRelation(e.reln),
                  gratuity:
                    e.gratuity === 0 || e.gratuity
                      ? String(e.gratuity)
                      : "",
                });
              });
            }
            merged.authorization = {
              name: userResp.name || "",
              place: sdf.place || "",
              date: preferSavedDate(
                merged.authorization && merged.authorization.date
                  ? merged.authorization.date
                  : formFData.authorization && formFData.authorization.date,
                todayIso
              ),
            };
            merged.acknowledge_employee = {
              authorization_name: userResp.name || "",
              date: merged.authorization.date,
            };
            merged.companyName = companyName;
            if (!isAdmin) {
              merged.certificate_employer = obj.certificate_employer;
              merged.passbook = obj.passbook;
              merged.passbook_filename = obj.passbook_filename;
            }
            if (!merged.employed_department) {
              merged.employed_department = obj.employed_department;
            }
            if (!merged.doa) {
              merged.doa = obj.doa;
            }
            setInitialValues(merged);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
          });
        })
        .catch(function (err) {
          console.log(err);
          if (isActive) {
            setLoading(false);
            toast.error("Unable to load Form F");
          }
        });

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  var fieldsReadOnly =
    isAdmin || isJoineeEmployeeFormLocked(formfFlagButton);
  var showCandidateActions =
    !isAdmin && !isJoineeEmployeeFormLocked(formfFlagButton);

  function payloadFromValues(values) {
    var accepted = Boolean(values.formFLegalConsent);
    var signedDate = preferSavedDate(
      values.authorization && values.authorization.date,
      new Date().toISOString()
    );
    return {
      date: values.date || null,
      nominees: (values.nominees || []).map(function (row) {
        return {
          name: String(row.name || "").trim(),
          address: String(row.address || "").trim(),
          reln: String(row.reln || "").trim(),
          age: row.age || null,
          gratuity: Number(row.gratuity),
        };
      }),
      employed_department: String(values.employed_department || "").trim(),
      ticket_no: String(values.ticket_no || "").trim(),
      doa: values.doa || null,
      permanentAddress: values.permanentAddress || {},
      authorization: {
        name: values.name || "",
        date: signedDate,
        place:
          values.authorization && values.authorization.place
            ? values.authorization.place
            : "",
      },
      witnessDetails: values.witnessDetails || {},
      certificate_employer: values.certificate_employer || {},
      acknowledge_employee: {
        date: signedDate,
      },
      passbook: values.passbook || "",
      passbook_filename: values.passbook_filename || "",
      formFLegalConsent: accepted,
      formFLegalConsentAt: accepted
        ? values.formFLegalConsentAt || new Date().toISOString()
        : null,
    };
  }

  function persistFormF(values) {
    var payload = payloadFromValues(values);
    if (values.formfId) {
      return FormfAPI.updateformf(userId, payload).then(function (res) {
        return { res: res, formfId: values.formfId };
      });
    }
    return FormfAPI.createformf(userId, payload).then(function (res) {
      var newId =
        res && res.data && res.data._id ? res.data._id : values.formfId;
      return { res: res, formfId: newId };
    });
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    return validateDraft(
      formik,
      buildValidationSchema(isAdmin, departmentLocked)
    ).then(function (ok) {
      return ok ? persistDraft(formik) : null;
    });
  }

  function persistDraft(formik) {
    var wrapped = pickSectionDraftData("formF", "main", formik.values);
    setBusy("draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    if (isAdmin) {
      if (formikHelpers && formikHelpers.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
      return;
    }
    setBusy("next");
    persistFormF(values)
      .then(function (result) {
        if (result && result.formfId && !values.formfId) {
          formikHelpers.setFieldValue("formfId", result.formfId);
        }
        if (
          values.formFLegalConsent &&
          !values.formFLegalConsentAt &&
          formikHelpers.setFieldValue
        ) {
          formikHelpers.setFieldValue(
            "formFLegalConsentAt",
            payloadFromValues(values).formFLegalConsentAt
          );
        }
        return deleteDraft().then(function () {
          return FormfAPI.formfStatus(userId);
        });
      })
      .then(function () {
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        setFormfFlagButton("submitted");
        toast.success("Form F submitted");
        navigate("/");
      })
      .catch(function (err) {
        console.log(err);
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save Form F");
      });
  }

  function handleAdminSaveCertificate(formik) {
    var cert = formik.values.certificate_employer || {};
    if (!isEmployerCertificateReady(cert)) {
      toast.error(
        "Please fill Date and Name and address of the establishment before saving"
      );
      return;
    }
    setBusy("admin");
    persistFormF(formik.values)
      .then(function () {
        return FormfAPI.getformf(userId);
      })
      .then(function (res) {
        var data = res && res.data ? res.data : {};
        var userResp = data.userResp || {};
        setFormfFlagButton(userResp.isFormFComplete || "");
        setBusy(false);
        setCertSaved(true);
        toast.success("Employer certificate saved");
      })
      .catch(function () {
        setBusy(false);
        toast.error("Unable to save employer certificate");
      });
  }

  function handlePassbookUpload(formik, e) {
    var file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }
    if (file.size > 1000000) {
      toast.error("File size must be less than 1MB");
      return;
    }
    var reader = new FileReader();
    reader.onload = function (ev) {
      formik.setFieldValue("passbook", ev.target.result);
      formik.setFieldValue("passbook_filename", file.name);
      setPassbookFileName(file.name);
    };
    reader.readAsDataURL(file);
  }

  function openPreview() {
    if (!userId) {
      return;
    }
    setPreviewLoading(true);
    DownloadFormHtmlToPdf.getform(userId, "FormFFormHtmlData")
      .then(function (res) {
        setPreviewLoading(false);
        if (res && res.success) {
          setPreviewHtml(res.data || "");
          setPreviewOpen(true);
        } else {
          toast.error("Unable to load preview");
        }
      })
      .catch(function () {
        setPreviewLoading(false);
        toast.error("Unable to load preview");
      });
  }

  function adminCertReady(values) {
    return isEmployerCertificateReady(values.certificate_employer);
  }

  function canShowAdminReview(values) {
    return (
      canShowHrReviewActions(formfFlagButton) &&
      (certSaved || adminCertReady(values))
    );
  }

  var formApprovedProps = {
    userId: props.id || routeId,
    formState: "isFormFComplete",
    nextURL: "AdminFormQ",
    approvedComment: approvedComment,
    setApprovedComment: setApprovedComment,
  };

  var formReSubmitProps = {
    userId: props.id || routeId,
    formState: "isFormFComplete",
    nextURL: "AdminFormQ",
    reSubmitComment: reSubmitComment,
    setReSubmitComment: setReSubmitComment,
  };

  if (loading || !initialValues) {
    return (
      <Box sx={{ width: "100%", py: 6, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            color: JOINEE_FORM_COLORS.label,
          }}
        >
          Loading Form F…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <Formik
        key={formKey}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={buildValidationSchema(isAdmin, departmentLocked)}
        onSubmit={handleSaveAndNext}
      >
        {function (formik) {
          var values = formik.values;
          var nomineeTotal = (values.nominees || []).reduce(function (acc, row) {
            return acc + Number((row && row.gratuity) || 0);
          }, 0);
          var arrayErrors = formik.errors || {};
          var signatureDisplay = values.userSignature
            ? { image: values.userSignature, fileName: values.userSignatureFileName }
            : { value: displayValue(values.name) };

          return (
            <Form noValidate>
              <ReportValue
                value={Boolean(isAdmin && canShowAdminReview(values))}
                onChange={props.onReviewReadyChange}
              />
              {isAdmin && !props.hideHrActions && canShowAdminReview(values) ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    mb: 1.5,
                    width: { xs: "100%", md: "82%" },
                    maxWidth: 780,
                    mx: "auto",
                  }}
                >
                  {canShowHrApprove(formfFlagButton) ? (
                    <ApprovedCustomizedDialogs
                      formApprovedProps={formApprovedProps}
                    />
                  ) : null}
                  {canShowHrResubmit(formfFlagButton) ? (
                    <ReSubmitCustomizedDialogs
                      formReSubmitProps={formReSubmitProps}
                    />
                  ) : null}
                </Box>
              ) : null}
              {isAdmin &&
              canShowHrReviewActions(formfFlagButton) &&
              !canShowAdminReview(values) ? (
                <Box
                  sx={{
                    width: { xs: "100%", md: "82%" },
                    maxWidth: 780,
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 13,
                      color: "#848F9A",
                      lineHeight: "20px",
                    }}
                  >
                    Complete and save the employer certificate below to unlock
                    Approve and Re-submit.
                  </Typography>
                </Box>
              ) : null}
              <JoineeFormShell
                banner={
                  !isAdmin && formfFlagButton === "resubmission required" ? (
                    <HrResubmitNotice comment={reSubmitComment} embedded />
                  ) : null
                }
                hideSteps
                onBack={
                  !isAdmin
                    ? function () {
                        navigate("/");
                      }
                    : null
                }
                title="Payment Of Gratuity Rules (Form 'F')"
                subtitle="Complete the gratuity nomination form by adding and verifying your nominee details."
                headerAction={
                  isAdmin ? (
                    previewLoading ? (
                      <LoadingButtons />
                    ) : (
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={openPreview}
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 400,
                          textTransform: "none",
                          color: JOINEE_FORM_COLORS.primary,
                          borderColor: JOINEE_FORM_COLORS.primary,
                          borderRadius: "6px",
                          minHeight: 32,
                          px: "12px",
                          bgcolor: "#FDFDFD",
                        }}
                      >
                        Preview Form
                      </Button>
                    )
                  ) : null
                }
                footer={
                  showCandidateActions ? (
                    <JoineeFormFooterActions
                      showPrevious
                      onPrevious={function () {
                        navigate("/");
                      }}
                      onDraft={function () {
                        return handleSaveDraft(formik);
                      }}
                      onNext={function () {
                        formik.validateForm().then(function (errors) {
                          if (errors && Object.keys(errors).length) {
                            notifyFormInvalid(errors);
                            formik.setTouched(
                              {
                                employed_department: true,
                                doa: true,
                                formFLegalConsent: true,
                                permanentAddress: {
                                  village: true,
                                  thane: true,
                                  subDivision: true,
                                  postOffice: true,
                                  district: true,
                                  state: true,
                                },
                                nominees: (values.nominees || []).map(
                                  function () {
                                    return {
                                      name: true,
                                      address: true,
                                      reln: true,
                                      age: true,
                                      gratuity: true,
                                    };
                                  }
                                ),
                              },
                              true
                            );
                            return;
                          }
                          formik.handleSubmit();
                        });
                      }}
                      nextLabel="Save and next"
                      disabled={Boolean(busy)}
                      drafting={busy === "draft"}
                      saving={busy === "next"}
                    />
                  ) : null
                }
              >
                <FormSectionTitle>Nomination</FormSectionTitle>
                <Box
                  sx={{
                    width: "100%",
                    mb: 3,
                    mt: 1,
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 14,
                    fontWeight: 400,
                    color: "#2E3338",
                    lineHeight: "22px",
                  }}
                >
                  <Typography
                    component="p"
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      mb: "16px",
                    }}
                  >
                    To,
                    <br />
                    {resolveCompanyName(values.companyName).toUpperCase()}
                    <br />
                    {resolveCompanyAddress(values.companyName)}
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      mb: "16px",
                    }}
                  >
                    I, Shri/Shrimati/Kumari{" "}
                    <Box component="span" sx={{ color: "#171A1C" }}>
                      {displayValue(values.name)}
                    </Box>{" "}
                    whose particulars are given in the statement below, hereby
                    nominate the person(s) mentioned below to receive the
                    gratuity payable after my death as also the gratuity standing
                    to my credit in the event of my death before that amount has
                    become payable, or having become payable has not been paid and
                    direct that the said amount of gratuity shall be paid in
                    proportion indicated against the name(s) of the nominee(s).
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      mb: "8px",
                    }}
                  >
                    I hereby certify that the person(s) mentioned is/are a
                    member(s) of my family within the meaning of clause (h) of
                    Section 2 of the Payment of Gratuity Act, 1972.
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      mb: "8px",
                    }}
                  >
                    I hereby declare that I have no family within the meaning of
                    clause (h) of Section 2 of the said Act.
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      mb: "8px",
                    }}
                  >
                    (a) My father/mother/parents is/are not dependent on me.
                    <br />
                    (b) My husband&apos;s father/mother/parents is/are not
                    dependent on my husband.
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      mb: "8px",
                    }}
                  >
                    I have excluded my husband from my family by a notice dated
                    the{" "}
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        mx: "6px",
                      }}
                    >
                      <FormDateField
                        id="formf-notice-date"
                        name="date"
                        placeholder="Select Date"
                        compact
                        disableFuture
                        value={values.date}
                        onChange={function (iso) {
                          formik.setFieldValue("date", iso);
                        }}
                        onBlur={formik.handleBlur}
                        disabled={fieldsReadOnly}
                      />
                    </Box>{" "}
                    to the controlling authority in terms of the provision to
                    clause (h) of Section 2 of the said Act.
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      mb: 0,
                    }}
                  >
                    Nomination made herein invalidates my previous nomination.
                  </Typography>
                </Box>

                <FieldArray name="nominees">
                  {function (arrayHelpers) {
                    var canAddNominee =
                      !fieldsReadOnly &&
                      (values.nominees || []).length < MAX_NOMINEES;
                    return (
                      <Box sx={{ mt: 3 }}>
                        <FormSectionTitle
                          action={
                            canAddNominee ? (
                              <FormAddButton
                                onClick={function () {
                                  arrayHelpers.push(emptyNominee());
                                }}
                              >
                                + Add Nominee
                              </FormAddButton>
                            ) : null
                          }
                        >
                          Nominee(s)
                        </FormSectionTitle>

                        {(values.nominees || []).map(function (row, index) {
                          return (
                            <Box key={"nom-" + index} sx={{ mb: 3.5 }}>
                              {index > 0 ? (
                                <FormSectionTitle
                                  action={
                                    !fieldsReadOnly ? (
                                      <RemoveRowButton
                                        onClick={function () {
                                          arrayHelpers.remove(index);
                                        }}
                                      />
                                    ) : null
                                  }
                                >
                                  Nominee {index + 1}
                                </FormSectionTitle>
                              ) : null}
                              <FormTwoCol>
                                <FormTextField
                                  id={"formf-nominee-name-" + index}
                                  rule="personName"
                                  name={"nominees." + index + ".name"}
                                  label="Name of the Nominee"
                                  placeholder="Enter Name of the Nominee"
                                  required
                                  value={row.name}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "nominees." + index + ".name"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                                <FormTextField
                                  id={"formf-nominee-address-" + index}
                                  rule="address"
                                  name={"nominees." + index + ".address"}
                                  label="Address of The Nominee"
                                  placeholder="Enter Address of The Nominee"
                                  required
                                  value={row.address}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "nominees." + index + ".address"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                              </FormTwoCol>
                              <FormTwoCol>
                                <FormSelectField
                                  id={"formf-nominee-reln-" + index}
                                  name={"nominees." + index + ".reln"}
                                  label="Nominee's Relationship with The Member"
                                  placeholder="Select Relationship"
                                  required
                                  value={row.reln}
                                  options={withRelationOption(row.reln)}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "nominees." + index + ".reln"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                                <FormTextField
                                  id={"formf-nominee-gratuity-" + index}
                                  rule="percentShare"
                                  name={"nominees." + index + ".gratuity"}
                                  label="Gratuity Shared (In %)"
                                  placeholder="e.g. 100"
                                  hint="1 to 100. The total across all nominees must be exactly 100."
                                  required
                                  value={row.gratuity}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "nominees." + index + ".gratuity"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                              </FormTwoCol>
                              <FormTwoCol>
                                <FormDateField
                                  id={"formf-nominee-dob-" + index}
                                  name={"nominees." + index + ".age"}
                                  label="Date of Birth"
                                  placeholder="Select Date of Birth"
                                  required
                                  disableFuture
                                  value={row.age}
                                  onChange={function (iso) {
                                    formik.setFieldValue(
                                      "nominees." + index + ".age",
                                      iso
                                    );
                                  }}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "nominees." + index + ".age"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                                <Box />
                              </FormTwoCol>
                            </Box>
                          );
                        })}

                        {typeof arrayErrors.nominees === "string" ? (
                          <Typography
                            sx={{
                              fontFamily: JOINEE_FORM_FONT,
                              fontSize: 12,
                              color: JOINEE_FORM_COLORS.error,
                              mb: 1,
                            }}
                          >
                            {arrayErrors.nominees}
                          </Typography>
                        ) : null}

                        <Typography
                          sx={{
                            fontFamily: JOINEE_FORM_FONT,
                            fontSize: 13,
                            color:
                              nomineeTotal === 100 ? "#1B7A4E" : "#848F9A",
                            mb: 1.5,
                          }}
                        >
                          Total share: {nomineeTotal}%
                          {nomineeTotal !== 100
                            ? " (must equal 100%)"
                            : ""}
                        </Typography>
                      </Box>
                    );
                  }}
                </FieldArray>

                <FormSectionTitle>Employee Details (Statement)</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Name of Employee:",
                      value: displayValue(values.emp_name),
                    },
                    {
                      label: "Gender:",
                      value: formatGender(values.gender),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Religion:",
                      value: displayValue(values.religion),
                    },
                    {
                      label: "Marital Status:",
                      value: formatMarital(values.maritalStatus),
                    },
                  ]}
                />
                <FormTwoCol>
                  <FormTextField
                    id="formf-employed-department"
                    rule="text"
                    name="employed_department"
                    label="Department"
                    placeholder="Enter Department"
                    required
                    value={values.employed_department}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "employed_department")}
                    disabled={
                      fieldsReadOnly ||
                      isHrLockedField(
                        isAdmin,
                        hrOnboardingSnapshot.designation
                      )
                    }
                  />
                  <FormTextField
                    id="formf-ticket-no"
                    rule="code"
                    name="ticket_no"
                    label="Ticket No."
                    placeholder="e.g. EMP-1024"
                    value={values.ticket_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "ticket_no")}
                    disabled={fieldsReadOnly}
                  />
                </FormTwoCol>
                <FormTwoCol>
                  <FormDateField
                    id="formf-doa"
                    name="doa"
                    label="Date of Appointment"
                    placeholder="Select Date"
                    required
                    disableFuture
                    value={values.doa}
                    onChange={function (iso) {
                      formik.setFieldValue("doa", iso);
                    }}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "doa")}
                    disabled={
                      fieldsReadOnly ||
                      isHrLockedField(isAdmin, hrOnboardingSnapshot.doj)
                    }
                  />
                  <Box />
                </FormTwoCol>

                <FormSectionTitle>Permanent Address</FormSectionTitle>
                <FormTwoCol>
                  <FormTextField
                    id="formf-village"
                    rule="place"
                    name="permanentAddress.village"
                    label="Village"
                    placeholder="Enter Village"
                    required
                    value={values.permanentAddress.village}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "permanentAddress.village")}
                    disabled={fieldsReadOnly}
                  />
                  <FormTextField
                    id="formf-thana"
                    rule="place"
                    name="permanentAddress.thane"
                    label="Thana"
                    placeholder="Enter Thana"
                    required
                    value={values.permanentAddress.thane}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "permanentAddress.thane")}
                    disabled={fieldsReadOnly}
                  />
                </FormTwoCol>
                <FormTwoCol>
                  <FormTextField
                    id="formf-subdivision"
                    rule="place"
                    name="permanentAddress.subDivision"
                    label="Sub-Division"
                    placeholder="Enter Sub-Division"
                    required
                    value={values.permanentAddress.subDivision}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "permanentAddress.subDivision")}
                    disabled={fieldsReadOnly}
                  />
                  <FormTextField
                    id="formf-postoffice"
                    rule="place"
                    name="permanentAddress.postOffice"
                    label="Post-Office"
                    placeholder="Enter Post-Office"
                    required
                    value={values.permanentAddress.postOffice}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "permanentAddress.postOffice")}
                    disabled={fieldsReadOnly}
                  />
                </FormTwoCol>
                <FormTwoCol>
                  <FormTextField
                    id="formf-district"
                    rule="place"
                    name="permanentAddress.district"
                    label="District"
                    placeholder="Enter District"
                    required
                    value={values.permanentAddress.district}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "permanentAddress.district")}
                    disabled={fieldsReadOnly}
                  />
                  <FormTextField
                    id="formf-state"
                    rule="place"
                    name="permanentAddress.state"
                    label="State"
                    placeholder="Enter State"
                    required
                    value={values.permanentAddress.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "permanentAddress.state")}
                    disabled={fieldsReadOnly}
                  />
                </FormTwoCol>

                <FormSectionTitle>Authorization</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    Object.assign(
                      { label: "Signature:" },
                      signatureDisplay
                    ),
                    {
                      label: "Place:",
                      value: displayValue(values.authorization.place),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Date:",
                      value: formatDob(values.authorization.date),
                    },
                  ]}
                />

                <FormSectionTitle>Declaration of witnesses</FormSectionTitle>
                <FormTwoCol>
                  <FormTextField
                    id="formf-witness-one"
                    rule="address"
                    name="witnessDetails.witnessOneNameAndAddress"
                    label="Witness one Name and Address"
                    placeholder="e.g. Rahul Sharma, 12 MG Road, Pune"
                    value={values.witnessDetails.witnessOneNameAndAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(
                      formik,
                      "witnessDetails.witnessOneNameAndAddress"
                    )}
                    disabled={fieldsReadOnly}
                  />
                  <FormTextField
                    id="formf-witness-one-sig"
                    rule="personName"
                    name="witnessDetails.witnessOneSignature"
                    label="Witness One Signature"
                    placeholder="Type full name, e.g. Rahul Sharma"
                    value={values.witnessDetails.witnessOneSignature}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "witnessDetails.witnessOneSignature")}
                    disabled={fieldsReadOnly}
                  />
                </FormTwoCol>
                <FormTwoCol>
                  <FormTextField
                    id="formf-witness-two"
                    rule="address"
                    name="witnessDetails.witnessTwoNameAndAddress"
                    label="Witness two Name and Address"
                    placeholder="e.g. Priya Patil, 5 FC Road, Pune"
                    value={values.witnessDetails.witnessTwoNameAndAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(
                      formik,
                      "witnessDetails.witnessTwoNameAndAddress"
                    )}
                    disabled={fieldsReadOnly}
                  />
                  <FormTextField
                    id="formf-witness-two-sig"
                    rule="personName"
                    name="witnessDetails.witnessTwoSignature"
                    label="Witness Two Signature"
                    placeholder="Type full name, e.g. Priya Patil"
                    value={values.witnessDetails.witnessTwoSignature}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "witnessDetails.witnessTwoSignature")}
                    disabled={fieldsReadOnly}
                  />
                </FormTwoCol>

                {isAdmin ? (
                  <Box sx={{ mt: 3 }}>
                    <FormSectionTitle>Certificate by the Employer</FormSectionTitle>
                    <Typography
                      sx={{
                        fontFamily: JOINEE_FORM_FONT,
                        fontSize: 14,
                        color: "#2E3338",
                        mb: 2,
                        lineHeight: "22px",
                      }}
                    >
                      Certified that the particulars of the above nomination have
                      been verified and recorded in this establishment.
                      Employer&apos;s Reference No. (if any)
                    </Typography>
                    <FormTwoCol>
                      <FormTextField
                        id="formf-cert-number"
                        name="certificate_employer.number"
                        rule="longCode"
                        label="Employer Reference No."
                        placeholder="Enter Reference No."
                        value={values.certificate_employer.number}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <FormDateField
                        id="formf-cert-date"
                        name="certificate_employer.date"
                        label="Date"
                        placeholder="Select Date"
                        value={values.certificate_employer.date}
                        onChange={function (iso) {
                          formik.setFieldValue(
                            "certificate_employer.date",
                            iso
                          );
                        }}
                        onBlur={formik.handleBlur}
                      />
                    </FormTwoCol>
                    <FormTwoCol>
                      <FormTextField
                        id="formf-cert-establishment"
                        name="certificate_employer.name_address_establishment"
                        rule="address"
                        label="Name and address of the establishment"
                        placeholder="Enter name and address"
                        value={
                          values.certificate_employer.name_address_establishment
                        }
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <Box />
                    </FormTwoCol>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Signature &amp; seal
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          textTransform: "none",
                        }}
                      >
                        Upload File
                        <input
                          hidden
                          accept="image/*"
                          type="file"
                          onChange={function (e) {
                            handlePassbookUpload(formik, e);
                          }}
                        />
                      </Button>
                      {passbookFileName ? (
                        <Typography
                          sx={{
                            fontFamily: JOINEE_FORM_FONT,
                            fontSize: 13,
                            mt: 1,
                          }}
                        >
                          {passbookFileName}
                        </Typography>
                      ) : null}
                    </Box>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={function () {
                        handleAdminSaveCertificate(formik);
                      }}
                      disabled={Boolean(busy)}
                      sx={{
                        fontFamily: JOINEE_FORM_FONT,
                        textTransform: "none",
                        bgcolor: JOINEE_FORM_COLORS.primary,
                        boxShadow: "none",
                      }}
                    >
                      Save employer certificate
                    </Button>
                  </Box>
                ) : null}

                <FormSectionTitle>Acknowledgement by the Employee</FormSectionTitle>
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 14,
                    fontWeight: 400,
                    color: "#848F9A",
                    mb: 2,
                    lineHeight: "22px",
                  }}
                >
                  Received the duplicate copy of nomination in Form &apos;F&apos;
                  filed by me and duly certified by the employer
                </Typography>
                <ReadOnlyRow
                  items={[
                    Object.assign(
                      { label: "Signature:" },
                      signatureDisplay
                    ),
                    {
                      label: "Date:",
                      value: formatDob(values.acknowledge_employee.date),
                    },
                  ]}
                />

                {!isAdmin ? (
                  <Box
                    sx={{ mt: 1, mb: 1 }}
                    data-ack-error={
                      errMsg(formik, "formFLegalConsent")
                        ? "true"
                        : undefined
                    }
                  >
                    <FormCheckboxField
                      name="formFLegalConsent"
                      checked={Boolean(values.formFLegalConsent)}
                      onChange={function (e) {
                        var checked = Boolean(e.target.checked);
                        formik.setFieldValue(
                          "formFLegalConsent",
                          checked,
                          true
                        );
                        formik.setFieldValue(
                          "formFLegalConsentAt",
                          checked
                            ? values.formFLegalConsentAt ||
                                new Date().toISOString()
                            : null,
                          false
                        );
                        formik.setFieldTouched(
                          "formFLegalConsent",
                          true,
                          false
                        );
                        if (checked) {
                          formik.setFieldError("formFLegalConsent", undefined);
                        }
                      }}
                      label="I confirm that the information provided is true and accurate, authorize the use of my previously submitted signature, and agree to all applicable terms and conditions."
                      disabled={fieldsReadOnly}
                      legal
                    />
                    {errMsg(formik, "formFLegalConsent") &&
                    !values.formFLegalConsent ? (
                      <Typography
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 12,
                          fontWeight: 400,
                          color: JOINEE_FORM_COLORS.error,
                          mt: 0.5,
                          ml: "28px",
                        }}
                      >
                        {errMsg(formik, "formFLegalConsent")}
                      </Typography>
                    ) : null}
                    <Box
                      sx={{
                        borderBottom:
                          "1px solid " + JOINEE_FORM_COLORS.border,
                        mt: 2,
                        mb: 1,
                      }}
                    />
                  </Box>
                ) : null}
              </JoineeFormShell>
            </Form>
          );
        }}
      </Formik>

      <Dialog
        open={previewOpen}
        onClose={function () {
          setPreviewOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontWeight: 600,
            pr: 6,
          }}
        >
          Preview Form
          <IconButton
            aria-label="close"
            onClick={function () {
              setPreviewOpen(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{ width: "100%", overflowX: "auto" }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default FormF;
